import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from './Layout';
import { colors } from '../constants/colors';

interface SellerLayoutProps {
  children: ReactNode;
  title?: string;
}

type TabId = 'overview' | 'shows' | 'orders' | 'shipments' | 'inventory' | 'shipping' | 'payouts' | 'banned-users';

const tabs: { id: TabId; label: string; path: string }[] = [
  { id: 'overview', label: 'Overview', path: '/sell' },
  { id: 'shows', label: 'Shows', path: '/sell/shows' },
  { id: 'orders', label: 'Orders', path: '/sell/orders' },
  { id: 'shipments', label: 'Shipments', path: '/sell/shipments' },
  { id: 'inventory', label: 'Inventory', path: '/sell/inventory' },
  { id: 'payouts', label: 'Payouts', path: '/sell/payouts' },
  { id: 'shipping', label: 'Shipping', path: '/sell/shipping' },
  { id: 'banned-users', label: 'Banned Users', path: '/sell/banned-users' },
];

export function SellerLayout({ children, title = 'Your Store' }: SellerLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from current path
  const getActiveTab = (): TabId => {
    if (location.pathname === '/sell') return 'overview';
    if (location.pathname.startsWith('/sell/sales')) return 'orders';
    if (location.pathname.startsWith('/sell/orders')) return 'orders';
    if (location.pathname.startsWith('/sell/shipments')) return 'shipments';
    if (location.pathname.startsWith('/sell/inventory')) return 'inventory';
    if (location.pathname.startsWith('/sell/shows')) return 'shows';
    if (location.pathname.startsWith('/sell/shipping')) return 'shipping';
    if (location.pathname.startsWith('/sell/payouts')) return 'payouts';
    if (location.pathname.startsWith('/sell/banned-users')) return 'banned-users';
    return 'overview';
  };

  const activeTab = getActiveTab();

  return (
    <Layout>
      <div style={styles.container}>
        <h1 style={styles.pageTitle}>{title}</h1>

        {/* Navigation Tabs */}
        <div style={styles.tabsContainer}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Page Content */}
        <div style={styles.content}>
          {children}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '16px',
  },
  tabsContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: '0',
  },
  tab: {
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    fontSize: '15px',
    fontWeight: 500,
    color: colors.gray[600],
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'color 0.15s, border-color 0.15s',
  },
  tabActive: {
    color: colors.brand.blue,
    borderBottomColor: colors.brand.blue,
    fontWeight: 600,
  },
  content: {
    maxWidth: '1200px',
  },
};
